/**
 * assignStepStatuses — Pure function that classifies each trip step as
 * `completed`, `in-progress`, or `upcoming` relative to the current time.
 *
 * Each step is treated as a half-open interval `[scheduledStart, scheduledEnd)`:
 *   - `completed`   iff `scheduledEnd != null && now >= scheduledEnd`
 *   - `upcoming`    iff `now < scheduledStart`
 *   - `in-progress` otherwise (`scheduledStart <= now` and not completed)
 *
 * A step with a resolvable `scheduledStart` but a null/unresolvable
 * `scheduledEnd` is never `completed`; once `now >= scheduledStart` it stays
 * `in-progress` (treated as never-completed).
 *
 * To guarantee at most one `in-progress` step (Req 4.6) even when derived
 * intervals abut or overlap, after per-step classification the function keeps
 * only the latest-starting step that qualifies as `in-progress` and demotes any
 * earlier still-`in-progress` steps to `completed` (they are temporally behind
 * the active one).
 *
 * The function is total (never throws), returns a new array preserving input
 * order, and does not mutate the input array or its step objects. Date instances
 * and numeric timestamps are supported for `scheduledStart`/`scheduledEnd`.
 *
 * Requirements: 4.1, 4.5, 4.6
 *
 * @typedef {'completed'|'in-progress'|'upcoming'} StepStatus
 */

/**
 * Convert a Date or numeric timestamp into comparable milliseconds.
 *
 * @param {Date|number|null|undefined} value
 * @returns {number|null} Milliseconds since epoch, or null when unresolvable
 */
function toMs(value) {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
}

/**
 * Classify a single step against `now` using the half-open interval rule.
 *
 * @param {{ scheduledStart: Date|number|null, scheduledEnd: Date|number|null }} step
 * @param {number|null} nowMs
 * @returns {StepStatus}
 */
function classify(step, nowMs) {
  const startMs = toMs(step?.scheduledStart);
  const endMs = toMs(step?.scheduledEnd);

  // Without a resolvable start or current time we cannot say the step has begun;
  // treat it as upcoming (the safest, never-backward classification).
  if (startMs == null || nowMs == null) {
    return 'upcoming';
  }

  // completed: has a resolvable end and now has reached/passed it.
  if (endMs != null && nowMs >= endMs) {
    return 'completed';
  }

  // upcoming: now is before the step starts.
  if (nowMs < startMs) {
    return 'upcoming';
  }

  // in-progress: started, and not completed (incl. null/never-completed end).
  return 'in-progress';
}

/**
 * Assign a `status` to every trip step relative to `now`.
 *
 * @param {Array<{ scheduledStart: Date|number|null, scheduledEnd: Date|number|null }>} steps
 *   The trip steps to classify.
 * @param {Date|number} now - The current time (Date or numeric timestamp).
 * @returns {Array<Object>} A new array of new step objects, each with a `status`
 *   field, in the same order as the input.
 */
export function assignStepStatuses(steps, now) {
  if (!Array.isArray(steps)) return [];

  const nowMs = toMs(now);

  // First pass: per-step classification (new objects, input untouched).
  const classified = steps.map((step) => ({
    ...step,
    status: classify(step, nowMs),
  }));

  // Second pass: enforce at most one `in-progress` by keeping the
  // latest-starting in-progress step and demoting earlier ones to `completed`.
  let latestIndex = -1;
  let latestStartMs = -Infinity;

  for (let i = 0; i < classified.length; i += 1) {
    if (classified[i].status !== 'in-progress') continue;

    const startMs = toMs(steps[i]?.scheduledStart);
    const effectiveStart = startMs == null ? -Infinity : startMs;

    // `>=` keeps the later array position on ties, yielding a deterministic
    // single active step.
    if (latestIndex === -1 || effectiveStart >= latestStartMs) {
      latestStartMs = effectiveStart;
      latestIndex = i;
    }
  }

  if (latestIndex !== -1) {
    for (let i = 0; i < classified.length; i += 1) {
      if (i !== latestIndex && classified[i].status === 'in-progress') {
        classified[i].status = 'completed';
      }
    }
  }

  return classified;
}

export default assignStepStatuses;
